import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import publicService from '../services/publicService';
import SEO from '../components/common/SEO';
import './Support.css';

// Simple inline icons so we don't add extra dependencies
const EmailIcon = () => (
  <svg
    className="support-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11Zm2.5-.5a.5.5 0 0 0-.5.5v.26l6 3.75 6-3.75V6.5a.5.5 0 0 0-.5-.5h-11Zm11 3.36-4.83 3.02a2.5 2.5 0 0 1-2.34 0L5.5 9.36V17.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V9.36Z"
      fill="currentColor"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg
    className="support-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M8.1 3.5A1.5 1.5 0 0 1 9.6 2h4.8A1.5 1.5 0 0 1 15.9 3.5v17a1.5 1.5 0 0 1-1.5 1.5H9.6a1.5 1.5 0 0 1-1.5-1.5v-17Zm1.5-.5a.5.5 0 0 0-.5.5v1h6v-1a.5.5 0 0 0-.5-.5H9.6ZM15.1 7h-6v10h6V7Zm-3 12.75a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z"
      fill="currentColor"
    />
  </svg>
);

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`faq-item ${open ? 'open' : ''}`}
      onClick={() => setOpen((prev) => !prev)}
    >
      <div className="faq-question">
        <span>{faq.question}</span>
        <span className="faq-toggle">{open ? '-' : '+'}</span>
      </div>
      {open && <div className="faq-answer">{faq.answer}</div>}
    </div>
  );
};

const Support = () => {
  const { user, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [supportInfo, setSupportInfo] = useState({
    email: '',
    phone: '',
    message: '',
  });
  const [faqs, setFaqs] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    orderNumber: '',
    subject: '',
    message: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchSupport = async () => {
      try {
        setLoading(true);
        setError('');

        const data = await publicService.getSupportContent();
        if (data?.support) {
          setSupportInfo(data.support);
        }
        setFaqs(Array.isArray(data?.faqs) ? data.faqs : []);

        // Pre-fill form from logged-in user
        if (isAuthenticated && user) {
          setFormData((prev) => ({
            ...prev,
            name: user.full_name || prev.name,
            email: user.email || prev.email,
            phone: user.phone_number || prev.phone,
          }));
        }
      } catch (err) {
        setError('Failed to load support information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSupport();
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in your name, email, and message.');
      return;
    }

    try {
      setSubmitting(true);
      await publicService.submitSupportRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        orderNumber: formData.orderNumber,
        subject: formData.subject,
        message: formData.message,
      });

      setSuccess(
        'Thank you for reaching out. Our support team will get back to you shortly.'
      );
      setFormData((prev) => ({
        ...prev,
        subject: '',
        message: '',
      }));
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        'Failed to send your message. Please try again.';
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <SEO
        title="Help & Support"
        description="Get help with your orders, products, and more at Essentick. Our support team is here to assist you with any questions."
        keywords="support, help, contact Essentick, FAQ, watch repair Nepal"
      />
      <div className="support-hero">
        <h1>Help &amp; Support</h1>
        <p>
          {supportInfo.message ||
            'Need help with your order or our products? Our team is here for you.'}
        </p>

        <div className="support-contact-summary">
          {supportInfo.email && (
            <div className="support-contact-item">
              <div className="support-contact-icon-wrapper">
                <EmailIcon />
              </div>
              <div>
                <span className="label">Email</span>
                <a href={`mailto:${supportInfo.email}`}>{supportInfo.email}</a>
              </div>
            </div>
          )}
          {supportInfo.phone && (
            <div className="support-contact-item">
              <div className="support-contact-icon-wrapper">
                <PhoneIcon />
              </div>
              <div>
                <span className="label">Phone</span>
                <a href={`tel:${supportInfo.phone}`}>{supportInfo.phone}</a>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="support-loading">Loading support information...</div>
      ) : (
        <div className="support-content">
          <section className="support-form-section">
            <h2>Contact our support team</h2>
            <p>
              Share your question or issue and we&apos;ll respond as soon as
              possible.
            </p>

            {error && <div className="support-alert support-alert-error">{error}</div>}
            {success && (
              <div className="support-alert support-alert-success">{success}</div>
            )}

            <form className="support-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full name *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="orderNumber">Order number (optional)</label>
                  <input
                    id="orderNumber"
                    name="orderNumber"
                    type="text"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    placeholder="If your question is about an order"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject (optional)</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your question or issue"
                  required
                />
              </div>

              <button
                type="submit"
                className="support-submit-button"
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </section>

          <section className="support-faq-section">
            <h2>FAQ</h2>
            <p className="support-faq-subtitle">Frequently asked questions</p>
            {faqs.length === 0 ? (
              <p className="support-faq-empty">
                No FAQs have been published yet. Please reach out via the contact
                form if you have any questions.
              </p>
            ) : (
              <div className="faq-list">
                {faqs.map((faq) => (
                  <FaqItem key={faq.id} faq={faq} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Support;

